import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Popconfirm, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import apiService from '@/services/api.service';
import type { ApiResponse } from '@/types/api';
import { logger } from '@/utils/logger';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { CreateEditInfoBlockModal } from '../CreateEditInfoBlockModal';

interface InfoBlock {
    id: number;
    title: string;
    description: string | null;
    link: string | null;
    icon: string | null;
    order: number;
    status: 'active' | 'inactive';
    createdAt: string;
    updatedAt: string;
}

const InfoBlocksPage: React.FC = () => {
    const [infoBlocks, setInfoBlocks] = useState<InfoBlock[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedBlock, setSelectedBlock] = useState<InfoBlock | null>(null);

    const fetchInfoBlocks = async () => {
        try {
            setIsLoading(true);
            const response = await apiService.get<ApiResponse<InfoBlock[]>>(
                `${API_ENDPOINTS.INFO_BLOCKS.BASE}?includeInactive=true`
            );
            setInfoBlocks(Array.isArray(response.data.data) ? response.data.data : []);
        } catch (error) {
            logger.error('Error fetching info blocks:', error);
            message.error('Ошибка при загрузке блоков информации');
            setInfoBlocks([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchInfoBlocks();
    }, []);

    const handleCreate = () => {
        setSelectedBlock(null);
        setIsModalVisible(true);
    };

    const handleEdit = (block: InfoBlock) => {
        setSelectedBlock(block);
        setIsModalVisible(true);
    };

    const handleDelete = async (id: number) => {
        try {
            await apiService.delete(API_ENDPOINTS.INFO_BLOCKS.DELETE(id));
            message.success('Блок информации успешно удален');
            fetchInfoBlocks();
        } catch (error) {
            logger.error('Error deleting info block:', error);
            message.error('Ошибка при удалении блока информации');
        }
    };

    const handleStatusChange = async (id: number, status: 'active' | 'inactive') => {
        try {
            await apiService.put(API_ENDPOINTS.INFO_BLOCKS.UPDATE(id), { status });
            message.success('Статус блока обновлен');
            fetchInfoBlocks();
        } catch (error) {
            logger.error('Error updating info block status:', error);
            message.error('Ошибка при обновлении статуса блока');
        }
    };

    const handleModalSuccess = () => {
        setIsModalVisible(false);
        setSelectedBlock(null);
        fetchInfoBlocks();
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
        setSelectedBlock(null);
    };

    const columns: ColumnsType<InfoBlock> = [
        {
            title: 'Порядок',
            dataIndex: 'order',
            key: 'order',
            width: 80,
            sorter: (a, b) => a.order - b.order,
        },
        {
            title: 'Название',
            dataIndex: 'title',
            key: 'title',
            sorter: (a, b) => a.title.localeCompare(b.title),
        },
        {
            title: 'Описание',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: (text: string | null) => text || '-',
        },
        {
            title: 'Ссылка',
            dataIndex: 'link',
            key: 'link',
            ellipsis: true,
            render: (text: string | null) => {
                if (!text) return '-';
                return (
                    <a href={text} target="_blank" rel="noopener noreferrer">
                        {text.length > 50 ? `${text.substring(0, 50)}...` : text}
                    </a>
                );
            },
        },
        {
            title: 'Иконка',
            dataIndex: 'icon',
            key: 'icon',
            width: 120,
            render: (text: string | null) => text || '-',
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status: 'active' | 'inactive', record: InfoBlock) => (
                <Switch
                    checked={status === 'active'}
                    onChange={(checked) => handleStatusChange(record.id, checked ? 'active' : 'inactive')}
                    checkedChildren="Активен"
                    unCheckedChildren="Неактивен"
                />
            ),
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 150,
            render: (_: unknown, record: InfoBlock) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        Редактировать
                    </Button>
                    <Popconfirm
                        title="Удалить блок информации?"
                        description="Это действие нельзя отменить"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Да"
                        cancelText="Нет"
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                        >
                            Удалить
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                >
                    Добавить блок информации
                </Button>
            </div>
            <Table
                columns={columns}
                dataSource={infoBlocks}
                rowKey="id"
                loading={isLoading}
                pagination={{
                    showSizeChanger: true,
                    showTotal: (total) => `Всего ${total} блоков`,
                }}
            />
            <CreateEditInfoBlockModal
                visible={isModalVisible}
                block={selectedBlock}
                onCancel={handleModalCancel}
                onSuccess={handleModalSuccess}
            />
        </div>
    );
};

export default InfoBlocksPage;
